import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from '@components/Provider';
import Loading from '@components/Loading';
import useClient from '@hooks/useClient';
import { IClientState } from '@modules/client/types';
import {
  CapaErrorCode,
  CapaServiceType,
  useAuthenticateLazyQuery,
  useFetchMyQuotationUserLazyQuery
} from '@generated/graphql';
import useUserActionLog, {
  Enum_Action_Page_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import { localStorage, STORAGE_ACTION, getStorageItem } from '@utils/storage';
import CommonLayout from '@routes/Common/CommonLayout';
import SignInPresenter from './SignInPresenter';
import { SignInForm } from './types';
import errorMessage from '@utils/errorMessage';

const SignInContainer: React.FC = () => {
  const isGhost = getStorageItem(STORAGE_ACTION.IS_GHOST_CLIENT);
  const redirectPath = getStorageItem(STORAGE_ACTION.REDIRECT_PATH);
  const history = useHistory();
  const [isKeepLoggedIn, setIsKeepLoggedIn] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { onInsertUserActionLogOne } = useUserActionLog();
  const { onGetClient, onUpdateToken, clientState } = useClient();
  const [authenticate, authenticateQuery] = useAuthenticateLazyQuery();
  const [
    fetchMyQuotationUser,
    fetchMyQuotationUserQuery
  ] = useFetchMyQuotationUserLazyQuery({
    fetchPolicy: 'network-only'
  });
  const localAccessToken = localStorage.getItem(
    STORAGE_ACTION.CLIENT_ACCESS_TOKEN
  );

  useLayoutEffect(() => {
    onInsertUserActionLogOne(
      Enum_Action_Page_Enum.Page_Login,
      CapaActivityType.PageVisit
    );
  }, [onInsertUserActionLogOne]);

  /** 유저 accessToken, refreshToken fetching */
  useEffect(() => {
    if (authenticateQuery.data) {
      const { data: authData } = authenticateQuery;
      const successSignin =
        authData?.authenticate.result === CapaErrorCode.Success;

      if (successSignin) {
        const { accessToken, refreshToken } = authData?.authenticate.data || {};
        if (!accessToken || !refreshToken) {
          enqueueSnackbar(errorMessage.login, { variant: 'error' });

          return;
        }

        onUpdateToken({
          accessToken: accessToken as string,
          refreshToken: refreshToken as string,
          isKeepLoggedIn
        });
        if (!isKeepLoggedIn) {
          localStorage.removeItem(STORAGE_ACTION.KEEP_LOGGED_IN);
        }
      } else {
        enqueueSnackbar(errorMessage.login, { variant: 'error' });
      }
    }
  }, [authenticateQuery, isKeepLoggedIn, enqueueSnackbar, onUpdateToken]);

  /** 이메일/패스워드를 통해 토큰을 받아온 후 클라이언트 정보 요청 */
  useEffect(() => {
    if (clientState.accessToken === localAccessToken) {
      fetchMyQuotationUser();
    }
  }, [clientState.accessToken, fetchMyQuotationUser, localAccessToken]);

  /** 클라이언트 정보 fetching 후 정보 업데이트 및 페이지 전환 */
  useEffect(() => {
    if (fetchMyQuotationUserQuery.data) {
      const { data: clientInfo } = fetchMyQuotationUserQuery;
      const successFetchMyQuotationUser =
        clientInfo?.fetchMyQuotationUser.result === CapaErrorCode.Success;

      if (successFetchMyQuotationUser) {
        const { data } = clientInfo?.fetchMyQuotationUser || {};
        onGetClient(data as Partial<IClientState>);
      }
      enqueueSnackbar('또 오셨군요, 환영합니다!', { variant: 'success' });

      if (redirectPath && !redirectPath.includes('login')) {
        history.replace(redirectPath, 'dashboard');
      } else {
        history.push('/dashboard');
      }
    } else if (
      fetchMyQuotationUserQuery?.error?.message.includes('NOT_ALLOWED_USER')
    ) {
      history.push('/dashboard', {
        fromSignIn: true
      });
    }
  }, [
    fetchMyQuotationUserQuery,
    history,
    redirectPath,
    enqueueSnackbar,
    onGetClient
  ]);

  /** 이미 로그인 되어있는 상태 */
  useEffect(() => {
    if (clientState && clientState.accessToken && clientState.refreshToken) {
      return history.push('/dashboard');
    }

    //eslint-disable-next-line
  }, []);

  const onSubmit = (req: SignInForm) => {
    if (isGhost && isGhost === 'true') return;
    setIsKeepLoggedIn(req.isKeepLoggedIn.length > 0);
    authenticate({
      variables: {
        email: req.email,
        password: req.password,
        service: CapaServiceType.Quotation
      }
    });
  };

  return (
    <CommonLayout gnbTabs={<></>} fullHeight>
      <Loading
        open={authenticateQuery.loading || fetchMyQuotationUserQuery.loading}
      />
      <SignInPresenter
        onSubmit={onSubmit}
        onSignUp={() => history.push('/signup')}
        onResetPassword={() => history.push('/login/requestpassword')}
      />
    </CommonLayout>
  );
};

export default SignInContainer;
